/**
 * @fileoverview Dynamic-band illustration: a dynamic component card with sample preview (Phase 5, J3).
 * @module ui-admin/help/illustrations/IllustrationDynamicBand
 *
 * Responsibilities: - Faithfully recreate a dynamic band card (DYNAMIC badge, expanding-list
 *                     description, "Preview with sample data" toggle, expanded sample rows) with
 *                     numbered callouts.
 * Dependencies:     - React; shared illustration primitives.
 * Architecture:     - Presentation-only SVG. No state, no data, no side effects.
 * Notes:            - Mirrors the DynamicAffordance block in DocumentCanvasBuilder.
 */

import React from 'react';
import { CalloutBadge, IllustrationFigure, ILLUS } from './illustrationPrimitives';

/**
 * @component IllustrationDynamicBand
 * Purpose: Annotated diagram of a dynamic band card and its sample-data preview.
 */
export function IllustrationDynamicBand() {
  return (
    <IllustrationFigure
      title="A dynamic band card with sample-data preview"
      viewBox="0 0 400 170"
      legend={[
        'DYNAMIC badge: this band expands with real data at render time.',
        'One-line description of what the list expands into.',
        'Preview with sample data toggle.',
        'Expanded sample rows shown when the toggle is on.',
      ]}
    >
      <rect x={2} y={2} width={396} height={166} rx={8} fill={ILLUS.gray100} stroke={ILLUS.gray200} />
      <rect x={20} y={16} width={360} height={138} rx={8} fill={ILLUS.white} stroke={ILLUS.gray300} />
      {/* header row */}
      <text x={34} y={40} fontSize={12} fontWeight={700} fill={ILLUS.gray800}>Violations list</text>
      <rect x={134} y={30} width={56} height={14} rx={3} fill={ILLUS.violet50} stroke={ILLUS.violet200} />
      <text x={162} y={40} textAnchor="middle" fontSize={8} fontWeight={700} fill={ILLUS.violet}>DYNAMIC</text>

      {/* dashed sample affordance box */}
      <rect x={34} y={52} width={332} height={92} rx={6} fill={ILLUS.violet50} stroke={ILLUS.violet200} strokeDasharray="4 3" />
      <text x={44} y={70} fontSize={9} fill={ILLUS.violet}>{'Repeats once per violation \u2014 expands to the full list.'}</text>
      {/* toggle */}
      <rect x={44} y={78} width={22} height={12} rx={6} fill={ILLUS.violet} />
      <circle cx={60} cy={84} r={4.5} fill={ILLUS.white} />
      <text x={72} y={88} fontSize={9} fill={ILLUS.gray700}>Preview with sample data</text>
      {/* sample rows */}
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x={44} y={100 + i * 13} width={312} height={10} rx={2} fill={ILLUS.white} stroke={ILLUS.gray200} />
          <circle cx={51} cy={105 + i * 13} r={1.6} fill={ILLUS.violet} />
          <rect x={58} y={102 + i * 13} width={120} height={5} rx={2} fill={ILLUS.gray200} />
          <rect x={188} y={102 + i * 13} width={150} height={5} rx={2} fill={ILLUS.gray100} />
        </g>
      ))}

      {/* Callouts */}
      <CalloutBadge n={1} x={162} y={26} />
      <CalloutBadge n={2} x={40} y={66} />
      <CalloutBadge n={3} x={40} y={84} />
      <CalloutBadge n={4} x={40} y={112} />
    </IllustrationFigure>
  );
}
